import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { requestApi, type MentorHistoryItem } from '../../api/request_api';

interface MentorHistoryCardProps {
  requestKey: string;
  isManager: boolean;
}

const ACTION_LABELS: Record<string, string> = {
  'mentor:claim': 'Claimed',
  'mentor:unclaim': 'Unclaimed',
  'mentor:approve': 'Approved',
  'mentor:deny': 'Denied',
};

const ACTION_ICONS: Record<string, React.ReactNode> = {
  'mentor:claim': <PersonIcon fontSize="small" color="primary" />,
  'mentor:unclaim': <PersonOffIcon fontSize="small" color="action" />,
  'mentor:approve': <CheckCircleIcon fontSize="small" color="success" />,
  'mentor:deny': <CancelIcon fontSize="small" color="error" />,
};

export function MentorHistoryCard({ requestKey, isManager }: MentorHistoryCardProps) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<MentorHistoryItem[]>([]);

  useEffect(() => {
    const loadHistory = async () => {
      if (!requestKey) return;
      setLoading(true);
      try {
        const response = await requestApi.getMentorHistory(requestKey);
        setHistory(response.items);
      } catch (err) {
        console.error('Failed to load mentor history:', err);
      } finally {
        setLoading(false);
      }
    };
    loadHistory();
  }, [requestKey]);

  // Don't show the card if there's no history
  if (!loading && history.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <HistoryIcon color="primary" />
          <Typography variant="h6">Mentor History</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {history.map((item) => (
              <Box
                key={item.uuid}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                {ACTION_ICONS[item.associated_type] || <PersonIcon fontSize="small" />}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    {isManager && item.user_uuid ? (
                      <Link href={`/admin/users/${item.user_uuid}`}>
                        {item.user_name || 'Unknown'}
                      </Link>
                    ) : (
                      item.user_name || 'Unknown'
                    )}
                    {' '}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {ACTION_LABELS[item.associated_type] || item.associated_type}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.date_created).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
